import React, { useState, useEffect, useContext } from 'react';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { 
  DocumentTextIcon,
  CalendarIcon,
  TrophyIcon,
  CogIcon,
  HomeIcon,
  ChatBubbleLeftRightIcon,
  BellIcon
} from '@heroicons/react/24/outline';
import { AuthContext } from '../../contexts/AuthContext';
import UpcomingEvents from './UpcomingEvents';

const CompanyPage = () => {
  const location = useLocation();
  const { currentUser } = useContext(AuthContext);
  
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // If we're at the root company path, redirect to feed
  if (location.pathname === '/company') {
    return <Navigate to="/company/feed" replace />;
  }

  const navItems = [
    { 
      name: 'Company Feed', 
      href: '/company/feed', 
      icon: HomeIcon,
      description: 'Latest company posts and announcements'
    },
    { 
      name: 'Posts', 
      href: '/company/posts', 
      icon: DocumentTextIcon,
      description: 'Browse and create company posts'
    },
    { 
      name: 'Events', 
      href: '/company/events', 
      icon: CalendarIcon,
      description: 'Company events and calendar'
    },
    { 
      name: 'Company Calendar', 
      href: '/company/calendar', 
      icon: CalendarIcon,
      description: 'Birthdays, holidays, and work anniversaries'
    },
    { 
      name: 'Leaderboard', 
      href: '/company/leaderboard', 
      icon: TrophyIcon,
      description: 'Employee engagement points and rankings'
    },
    { 
      name: 'Required Reading', 
      href: '/company/required-reading', 
      icon: BellIcon,
      description: 'Important posts that require acknowledgment'
    }
  ];

  // Add admin panel for admin users
  if (currentUser && currentUser.role_type === 'admin') {
    navItems.push({
      name: 'Admin Panel',
      href: '/company/admin',
      icon: CogIcon,
      description: 'Manage categories, event types, and point settings'
    });
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Company Page</h1>
              <p className="mt-1 text-sm text-gray-500">
                Stay connected with company news, events, and team activities
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 flex-shrink-0">
            <nav className="space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      active
                        ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                        : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    } group flex items-start px-3 py-3 text-sm font-medium border-l-4 transition-colors duration-200`}
                  >
                    <Icon
                      className={`${
                        active ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500'
                      } flex-shrink-0 mr-3 h-6 w-6`}
                      aria-hidden="true"
                    />
                    <div>
                      <div className="font-medium">{item.name}</div>
                      <div className="text-xs text-gray-500 mt-1">{item.description}</div>
                    </div>
                  </Link>
                );
              })}
            </nav>

            {/* Upcoming Events Card */}
            <div className="mt-8">
              <UpcomingEvents />
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyPage; 